import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useSeedActiveOrgFromSession } from '../active-org.ts';
import { SIGN_IN_URL, SIGN_OUT_URL, SIGN_UP_URL, useAuth } from '../api.ts';
import { OrgSwitcher } from '../components/OrgSwitcher.tsx';
import {
  AccountingIcon,
  ChevronLeftIcon,
  LogoMark,
  ResilienceIcon,
  RoadmapIcon,
  SettingsIcon,
  TalentIcon,
  TimelineIcon,
} from '../snapshot/components/Icons.tsx';

import styles from './Layout.module.css';

// One sidebar for the whole app. The logo at the top is the open/close
// button for the menu; the org switcher in the footer is permanent chrome.
//
// The menu has two states, toggled by the logo:
//   • collapsed — the six section icons (current section highlighted)
//   • open      — the section's expanded panel
//
// "Open" is contextual: on a settings route it's the settings submenu; on
// any other route it's the labelled primary menu (the same six items with
// labels). This is the reusable submenu pattern — give another route its
// own submenu and the same logo toggle drives it. The collapsed icons and
// the labelled primary menu share one DOM structure (labels are just
// hidden when collapsed), and the icon x / item height never change with
// the rail width, so expanding and contracting only animate the width —
// the icons never jump.
//
// Activity dropped from the nav — its content now lives inside the
// Timeline view's left control panel. The /activity route still resolves
// for anyone who bookmarked it.
const PRIMARY_NAV = [
  { to: '/resilience', label: 'Resilience', Icon: ResilienceIcon },
  { to: '/talent', label: 'Talent', Icon: TalentIcon },
  { to: '/timeline', label: 'Timeline', Icon: TimelineIcon },
  { to: '/roadmap', label: 'Roadmap', Icon: RoadmapIcon },
  { to: '/accounting', label: 'Accounting', Icon: AccountingIcon },
  { to: '/setup', label: 'Settings', Icon: SettingsIcon },
];

// Settings submenu, grouped. "Settings" (org configuration) sits above
// "Account" (personal pages).
const SETTINGS_NAV = [
  {
    heading: 'Settings',
    items: [
      { to: '/setup/integrations', label: 'Integrations' },
      { to: '/setup/manage-orgs', label: 'Manage Orgs' },
      { to: '/setup/platform-map', label: 'Platform Maps' },
      { to: '/setup/engineers', label: 'Engineers' },
      { to: '/setup/link-repo', label: 'Link New Repo' },
    ],
  },
  {
    heading: 'Account',
    items: [
      { to: '/settings/profile', label: 'Profile' },
      { to: '/settings/team', label: 'Team' },
    ],
  },
];

export interface LayoutProps {
  // Optional explicit document title. When used as a layout route (no
  // children) the title is derived from whether we're on a settings page.
  title?: string;
  children?: ReactNode;
}

export function Layout({ title, children }: LayoutProps) {
  const auth = useAuth();
  const signedIn = auth.status === 'signed-in';

  // Honor the org picked at sign-in: seed the active-org store from it on
  // first load (no-op once the user has an active org stored).
  useSeedActiveOrgFromSession(
    signedIn ? auth.me.selected_org_slug : null,
    signedIn ? auth.me.orgs : [],
  );

  const { pathname } = useLocation();
  const inSettings = pathname.startsWith('/settings') || pathname.startsWith('/setup');

  // Whether the menu is open (wide) or collapsed to the six icons. The
  // logo toggles it. Arriving on a settings route opens its submenu.
  const [menuOpen, setMenuOpen] = useState(inSettings);
  // In settings, "‹ Back" swaps the submenu for the labelled primary menu
  // (same width) without leaving the page — a quick jump to another
  // section. Reset whenever we leave settings so the next entry lands on
  // the submenu.
  const [settingsShowPrimary, setSettingsShowPrimary] = useState(false);
  const prevInSettings = useRef(inSettings);
  useEffect(() => {
    if (inSettings && !prevInSettings.current) setMenuOpen(true);
    if (!inSettings) setSettingsShowPrimary(false);
    prevInSettings.current = inSettings;
  }, [inSettings]);

  const wide = menuOpen;
  const showSettingsSubmenu = inSettings && menuOpen && !settingsShowPrimary;

  useEffect(() => {
    document.title =
      title ?? (inSettings ? 'Settings · Technical Accounting' : 'Technical Accounting');
  }, [title, inSettings]);

  const settingsItemClass = ({ isActive }: { isActive: boolean }) =>
    `${styles.settingsItem}${isActive ? ` ${styles.active}` : ''}`;

  return (
    <>
      <aside
        className={`${styles.navRail}${wide ? ` ${styles.navRailWide}` : ''}`}
        aria-label="Primary"
      >
        {/* The logo opens/closes the menu. */}
        <button
          type="button"
          className={styles.navLogo}
          onClick={() => {
            setSettingsShowPrimary(false);
            setMenuOpen((o) => !o);
          }}
          aria-label={menuOpen ? 'Collapse menu' : 'Expand menu'}
          aria-expanded={menuOpen}
        >
          <LogoMark />
        </button>

        {showSettingsSubmenu ? (
          <nav className={styles.settingsNav} aria-label="Settings">
            <button
              type="button"
              className={`${styles.backBtn} secondary-function`}
              onClick={() => setSettingsShowPrimary(true)}
              aria-label="Back to menu"
            >
              <ChevronLeftIcon />
              Back
            </button>
            {SETTINGS_NAV.map((group) => (
              <div key={group.heading} className={styles.settingsGroup}>
                <div className={styles.settingsHeading}>{group.heading}</div>
                {group.items.map(({ to, label }) => (
                  <NavLink key={to} to={to} className={settingsItemClass}>
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        ) : (
          <nav className={styles.primaryNav} aria-label="Views">
            {PRIMARY_NAV.map(({ to, label, Icon }) => {
              const isSettingsItem = to === '/setup';
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `${styles.primaryItem}${
                      isActive || (isSettingsItem && inSettings) ? ` ${styles.active}` : ''
                    }`
                  }
                  title={wide ? undefined : label}
                  aria-label={label}
                  onClick={(e) => {
                    // Clicking the (highlighted) Settings icon while already
                    // in settings re-opens the submenu in place.
                    if (isSettingsItem && inSettings) {
                      e.preventDefault();
                      setSettingsShowPrimary(false);
                      setMenuOpen(true);
                    }
                  }}
                >
                  <Icon />
                  <span className={styles.primaryLabel}>{label}</span>
                </NavLink>
              );
            })}
          </nav>
        )}

        <div className={styles.navFooter}>
          {signedIn ? (
            <OrgSwitcher email={auth.me.user.email} signOutUrl={SIGN_OUT_URL} />
          ) : auth.status === 'signed-out' ? (
            <>
              <a href={SIGN_IN_URL} className={styles.navItem}>
                Sign in
              </a>
              <a href={SIGN_UP_URL} className={styles.navItem}>
                Sign up
              </a>
            </>
          ) : null}
        </div>
      </aside>

      <main className={`${styles.content}${wide ? ` ${styles.contentWide}` : ''}`}>
        {children ?? <Outlet />}
      </main>
    </>
  );
}
